import { ReactElement } from "react";

import { LoadingSpinner } from "@newsela/angelou";
import { BookmarkContextProvider } from "@/users/useBookmarkContent/useBookmarkContent";
import { ContentPromotionFormResponse } from "@/types/models/ContentPromotionFormResponse.interface";
import { UserRoleEnum } from "@/users/getHighestRoleLevel/getHighestRoleLevel.constants";
import { ContentPromotionForm } from "@/types/models";
import {
  ADMIN_CONSOLE_CONTENT_PROMOTION_PATHNAME,
  CHIRON_CONTENT_PROMOTION_PATHNAME,
} from "../constants";
import { ContentPromotionContentSource } from "../types/ContentPromotionQuestionResponseSource.enum";
import { ContentPromotionVenue } from "../types/ContentPromotionVenue.enum";
import ContentPreviewRow from "./ContentPreviewRow";
import {
  noReponsesMessageContainerCss,
  previewSubtitleCss,
  previewTitleContainerCss,
  previewTitleCss,
  rowsContainerCss,
  wrapperCss,
} from "./ContentPreviewContainer.style";

interface ContentPreviewContainerProps {
  formResponses: ContentPromotionFormResponse[] | undefined;
  title?: string | undefined;
  subtitle?: string | undefined;
  showEditButton?: boolean;
  renderSectionBelowTitle?: ReactElement;
  selectedFormId?: ContentPromotionForm["id"] | undefined;
  isLoadingFormResponses: boolean;
  venue: ContentPromotionVenue;
  mostSeniorUserRole?: UserRoleEnum | undefined;
  isStaffUser?: boolean;
}

const ContentPreviewContainer = ({
  isStaffUser,
  mostSeniorUserRole,
  formResponses,
  title,
  subtitle,
  renderSectionBelowTitle,
  selectedFormId,
  showEditButton = false,
  isLoadingFormResponses,
  venue,
}: ContentPreviewContainerProps) => {
  const contentPreviewRows = formResponses
    ?.filter(({ response }) => Array.isArray(response) && response.length)
    .map((formResponse) => {
      return {
        responses: formResponse.response,
        contentSource: formResponse.platform_alignment_question
          .content_source as ContentPromotionContentSource,
        formId: formResponse.platform_alignment_form.id,
        questionNumber:
          formResponse.platform_alignment_question.question_number,
        title: formResponse.platform_alignment_question.question_type,
      };
    })
    .sort(
      (rowA, rowB) => Number(rowA.questionNumber) - Number(rowB.questionNumber),
    );

  const NoResponsesMessage = () => {
    if (!selectedFormId) return <></>;

    const shouldSeeMessageToEditForm =
      (venue === ContentPromotionVenue.school &&
        mostSeniorUserRole === UserRoleEnum.school_admin) ||
      (venue === ContentPromotionVenue.district &&
        mostSeniorUserRole === UserRoleEnum.district_admin);

    const pathname = isStaffUser
      ? CHIRON_CONTENT_PROMOTION_PATHNAME
      : ADMIN_CONSOLE_CONTENT_PROMOTION_PATHNAME;

    const editFormHref = `${pathname}/form/${selectedFormId}/?question_number=1`;
    const messageToEditForm = (
      <span>
        Click <a href={editFormHref}>here</a> to complete the form
      </span>
    );

    return (
      <div css={noReponsesMessageContainerCss}>
        The {venue} has yet to submit responses for this configuration.{" "}
        {shouldSeeMessageToEditForm ? messageToEditForm : null}
      </div>
    );
  };

  return (
    <BookmarkContextProvider>
      <div css={wrapperCss}>
        <div css={previewTitleContainerCss}>
          {title && <div css={previewTitleCss}>{title}</div>}
          {subtitle && <div css={previewSubtitleCss}>{subtitle}</div>}
          {renderSectionBelowTitle && renderSectionBelowTitle}
        </div>
        {isLoadingFormResponses ? (
          <LoadingSpinner />
        ) : (
          <div css={rowsContainerCss}>
            {contentPreviewRows && contentPreviewRows?.length > 0 ? (
              contentPreviewRows?.map((rowContent, index) => (
                <ContentPreviewRow
                  key={index}
                  rowContent={rowContent}
                  showEditButton={showEditButton}
                />
              ))
            ) : (
              <NoResponsesMessage />
            )}
          </div>
        )}
      </div>
    </BookmarkContextProvider>
  );
};

export default ContentPreviewContainer;
